import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { api } from '../api/client.ts'
import { Avatar } from './Avatar.tsx'
import { useChats } from '../store/chats.ts'
import { plural } from '../lib/format.ts'

/**
 * ОТПРАВИТЬ В ПЕРЕПИСКУ
 *
 * Мессенджер — главная поверхность приложения: обсуждают в нём, а разделы
 * хранят. Поэтому у любого объекта — списка покупок, события, фильма —
 * есть способ попасть в переписку карточкой.
 *
 * Карточка живая: в ней видно, сколько куплено прямо сейчас, а не сколько
 * было в момент отправки. Поэтому отправляем ссылку на объект, а не текст
 * о нём — пересланный текст устарел бы через час.
 *
 * Подпись необязательна: чаще всего достаточно самой карточки.
 */
export function SendToChatDialog({
  entityType,
  entityId,
  title,
  onClose,
}: {
  entityType: string
  entityId: string
  /** Название объекта — чтобы человек видел, что именно отправляет */
  title: string
  onClose: () => void
}) {
  const chats = useChats((s) => s.chats)
  const loadChats = useChats((s) => s.loadChats)
  const navigate = useNavigate()

  const [note, setNote] = useState('')
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const send = async (chatId: string) => {
    setBusy(chatId)
    setError(null)
    try {
      await api(`/api/chats/${chatId}/messages`, {
        method: 'POST',
        body: {
          // Пустую подпись не отправляем вовсе: сервер ждёт либо текст,
          // либо ничего, но не пустоту
          ...(note.trim() ? { body: note.trim() } : {}),
          embeds: [{ entityType, entityId }],
        },
      })
      await loadChats()
      onClose()
      // Переходим в переписку: иначе непонятно, дошло ли
      navigate(`/chat/${chatId}`)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не получилось отправить')
    } finally {
      setBusy(null)
    }
  }

  return (
    <div className="sheet" onClick={onClose}>
      <div className="sheet-body" onClick={(e) => e.stopPropagation()}>
        <div className="sheet-head">
          <h2>Отправить в переписку</h2>
          <button className="icon-btn" onClick={onClose} aria-label="Закрыть">
            ✕
          </button>
        </div>

        <p className="sheet-text">«{title}» — выбери, куда</p>

        <label className="field">
          <span className="field-label">Подпись — если хочешь что-то добавить</span>
          <input
            className="input"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Не обязательно"
            maxLength={500}
          />
        </label>

        <div className="member-list">
          {chats.length === 0 && <p className="comments-empty">Переписок пока нет</p>}

          {chats.map((chat) => (
            <button
              key={chat.id}
              className="member-row pick"
              onClick={() => void send(chat.id)}
              disabled={busy !== null}
            >
              <Avatar id={chat.id} name={chat.title} url={chat.avatarUrl} size={40} />
              <div className="member-text">
                <div className="member-name">{chat.title}</div>
                {chat.kind !== 'DIRECT' && (
                  <div className="member-sub">
                    {chat.membersCount}{' '}
                    {plural(chat.membersCount, 'участник', 'участника', 'участников')}
                  </div>
                )}
              </div>
              {busy === chat.id && <span className="spinner" style={{ marginLeft: 'auto' }} />}
            </button>
          ))}
        </div>

        {error && <div className="sheet-error">{error}</div>}
      </div>
    </div>
  )
}
